import React from 'react';
import PropTypes from 'prop-types';

import PhotoRow from './photo_row';
import PhotoUploader from './photo_uploader';
import { reorderableList } from 'helpers/react_dnd/reorderable_list';
import PhotoEditorServiceModal from '../modals/photo_editor_service_modal';
import confirm from 'components/confirm';
import { NEW_PHOTO_ATTACHMENT_ID_PREFIX } from 'helpers/photos/constants';

export const photoFormPropTypes = {
  classSlug: PropTypes.string.isRequired,
  defaultPhotoOption: PropTypes.string.isRequired,
  photoAttachments: PropTypes.array.isRequired,
  photoTypeOptions: PropTypes.array.isRequired,
  photoEditorUrl: PropTypes.string,
  syndication: PropTypes.bool.isRequired
};

export class PhotoForm extends React.Component {
  static propTypes = {
    ...photoFormPropTypes,
    bulkDownloadPhotosLink: PropTypes.string,
    maxPhotos: PropTypes.number,
    showEdit: PropTypes.bool,
    showLimit: PropTypes.bool,
    onPhotoChange: PropTypes.func
  };

  static defaultPropTypes = {
    showEdit: true,
    showLimit: false
  }

  state = {
    // TODO: this component is managing it's own state for the photo attachments and so is media tab. It would create
    // fewer problems if we just tracked it with the media tab so state wouldn't have to update twice when photos
    // change.
    photoAttachments: this.props.photoAttachments,
    photoToEdit: null,
    selectedPhotoIds: []
  };

  showSyndicationPhotoWarning() {
    return !this.state.photoAttachments.some(pa => pa.syndicate);
  }

  rowKey = (photoAttachment) => {
    const { id, uploadId, photoId, copy } = photoAttachment;

    if (id) return id;
    if (copy) return `c${photoId}`;
    if (uploadId) return `u${uploadId}`;
  };

  allowUpload = () => {
    if (this.props.maxPhotos && this.state.photoAttachments.length >= this.props.maxPhotos) {
      alert(`Cannot exceed ${this.props.maxPhotos} photos`);
      return false;
    } else {
      return true;
    }
  };

  handlePhotoAttachmentsUpdate = (photoAttachments) => {
    if (this.props.onPhotoChange) {
      this.props.onPhotoChange(photoAttachments);
    }
  };

  handleAttributeChange = (index, attribute, value) => {
    this.setState((state) => {
      const photoAttachments = [...state.photoAttachments];
      photoAttachments[index] = { ...photoAttachments[index], [attribute]: value };
      this.handlePhotoAttachmentsUpdate(photoAttachments);

      return { photoAttachments };
    });
  };

  handleBulkAttributeChange = (attribute, value) => {
    this.setState((previousState) => {
      const updatedPhotoAttachments = previousState.photoAttachments.map((photoAttachment) => {
        if (previousState.selectedPhotoIds.includes(this.rowKey(photoAttachment))) {
          return { ...photoAttachment, [attribute]: value };
        }

        return photoAttachment;
      });
      this.handlePhotoAttachmentsUpdate(updatedPhotoAttachments);
      return { photoAttachments: updatedPhotoAttachments, selectedPhotoIds: [] };
    });
  };

  handleUpload = (data) => {
    this.setState((state) => {
      const photoAttachments = [
        ...state.photoAttachments,
        {
          url: data.url,
          thumbnailUrl: data.url,
          originalUploadId: data.id,
          uploadId: data.id, //unnecessary?
          uploaded: true,
          dataContentType: data.file_type,
          id: `${NEW_PHOTO_ATTACHMENT_ID_PREFIX}${data.id}`
        }
      ];
      this.handlePhotoAttachmentsUpdate(photoAttachments);
      return { photoAttachments };
    });
  };

  handlePhotoReorder = (dragIndex, hoverIndex) => {
    this.setState((state) => {
      const photoAttachments = [...state.photoAttachments];
      const dragPhoto = photoAttachments[dragIndex];
      // in rare cases, dragPhoto can return undefined.
      // we are unsure on why it happens, but it seems like a very rare edge case.
      if (!dragPhoto) {
        this.handlePhotoAttachmentsUpdate(photoAttachments);
        return { photoAttachments };
      }

      photoAttachments.splice(dragIndex, 1);
      photoAttachments.splice(hoverIndex, 0, dragPhoto);
      photoAttachments.forEach((photoAttachment, index) => {
        photoAttachment.sortOrder = index;
      });
      this.handlePhotoAttachmentsUpdate(photoAttachments);

      return { photoAttachments };
    });
  };

  handleCloseEditModal = () => {
    this.setState({ photoToEdit: null });
  };

  handleOpenEditModal = (photoToEdit) => {
    this.setState({ photoToEdit });
  };

  handlePhotoEditSave = (photoUpdate) => {
    const photoToEdit = this.state.photoAttachments.find(photo => photo.id === this.state.photoToEdit.id);
    const photoToEditIndex = this.state.photoAttachments.indexOf(photoToEdit);

    this.handleAttributeChange(photoToEditIndex, 'uploadId', photoUpdate.id);
    this.handleAttributeChange(photoToEditIndex, 'thumbnailUrl', photoUpdate.url);
    this.handleAttributeChange(photoToEditIndex, 'url', photoUpdate.url);
    this.setState({ photoToEdit: null });
  };

  toggleSyndicated = (syndicate) => {
    this.state.photoAttachments.forEach((pa, index) => {
      this.handleAttributeChange(index, 'syndicate', syndicate);
    });
  };

  handleSyndicateAllChange = (allSyndicated) => {
    const toggle = () => this.toggleSyndicated(!allSyndicated);
    const message = <div>
      In order to syndicate this listing, you must confirm you have the rights to syndicate.
      <br/><br/>
      By clicking yes below, you are confirming you have the rights to publish all these photos on third party sites
    </div>;

    allSyndicated ? toggle() : confirm(message,
      {
        title: 'Confirm photo rights',
        buttonLabel: 'Yes, I have the rights',
        buttonStyle: 'save'
      }
    ).then(toggle);
  };

  renderPhotoEditor() {
    if (!this.props.photoEditorUrl) return;

    return (
      <PhotoEditorServiceModal
        imageUrl={this.state.photoToEdit ? this.state.photoToEdit.url : ''}
        photoEditorUrl={this.props.photoEditorUrl}
        show={!!this.state.photoToEdit}
        onHide={this.handleCloseEditModal}
        onSave={this.handlePhotoEditSave}
      />
    );
  }

  renderSyndicateAllButton() {
    const { photoAttachments } = this.state;
    const show = photoAttachments.some(pa => !pa.deleted);
    if (!show) return;

    const allSyndicated = !photoAttachments.some(pa => !pa.syndicate);
    return <div className="form-group boolean optional text-right border-0">
      <div className="checkbox">
        <label className="boolean optional">
          <input
            checked={allSyndicated}
            name="syndicate-all"
            type="checkbox"
            onChange={() => this.handleSyndicateAllChange(allSyndicated)}
          />
          Approve all?
        </label>
      </div>
    </div>;
  }

  renderNoSyndicatablePhotosWarning() {
    const showWarning = this.props.syndication && this.showSyndicationPhotoWarning();
    if (!showWarning) return;

    return <div className="alert alert-warning mb-1" role="alert">
      <span>
        Your listing photos will not be included on third party listing
        sites or Buildout Buyer Network unless they are approved.
      </span>

      <strong> Please review and check the appropriate boxes below for any approved photos.</strong>
    </div>;
  }

  render() {
    return (
      <React.Fragment>
        {this.renderNoSyndicatablePhotosWarning()}
        <div className="form-group photos">
          {this.renderSyndicateAllButton()}
          <div className="old-media-tab">
            {this.state.photoAttachments.map((photoAttachment, index) => (
              <PhotoRow
                classSlug={this.props.classSlug}
                defaultPhotoOption={this.props.defaultPhotoOption}
                index={index}
                key={this.rowKey(photoAttachment)}
                photoAttachment={photoAttachment}
                photoEditorUrl={this.props.photoEditorUrl}
                photoTypeOptions={this.props.photoTypeOptions}
                showEdit={this.props.showEdit}
                userAdmin={this.props.userAdmin}
                onAttributeChange={this.handleAttributeChange}
                onEdit={this.handleOpenEditModal}
                onReorder={this.handlePhotoReorder}
              />
            ))}
            <PhotoUploader
              allowUpload={this.allowUpload}
              onUpload={this.handleUpload}
            />
            {this.renderPhotoEditor()}
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default reorderableList(PhotoForm);
